/* eslint-disable sort-keys-fix/sort-keys-fix */
import { PayloadAction, createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { actionFetchProducts } from '@lib/core/products/slices/searchCatalog';
import { getProductsListData } from '@lib/core/products/utils';
import { ApiQueries, ApiValues } from '@lib/core/service/consts';
import { IFetchProductsOptions } from '@lib/core/service/types/interface';
import { IProductRatingOptions, actionGetProductRateOptions } from '@lib/core/users/slices/productRate';

export const FilterConsts = {
  ordering: {
    type: 'ordering',
    query: ApiQueries.ordering,
  },
  character: {
    type: 'character',
    query: ApiQueries.character,
  },
  promotion: {
    type: 'promotion',
    query: ApiQueries.promotions,
  },
  characteristics: {
    type: 'characteristics',
    query: ApiQueries.characteristics,
  },
  formats: {
    type: 'formats',
    query: ApiQueries.formats,
  },
  priceRangeMax: {
    type: 'price',
    query: ApiQueries.priceMax,
  },
  priceRangeMin: {
    type: 'price',
    query: ApiQueries.priceMin,
  },
  moods: {
    type: 'moods',
    query: ApiQueries.moods,
  },
  rating: {
    type: 'rating',
    query: ApiQueries.productIdentifier,
  },
  wishlist: {
    type: 'wishList',
    query: ApiQueries.productIdentifier,
  },
  search: {
    type: 'search',
    query: ApiQueries.productName,
  },
  origins: {
    type: 'regions',
    query: ApiQueries.origins,
  },
  styles: {
    type: 'styles',
    query: ApiQueries.styles,
  },
} as const;

export interface ISetPriceRangeValues {
  appMinPriceRangeValue?: number;
  appMaxPriceRangeValue?: number;
  userLowerPriceRangeValue?: number;
  userUpperPriceRangeValue?: number;
  isManual?: boolean;
}

interface ISetPriceRangeValuesPayload {
  payload: ISetPriceRangeValues;
}

export type ISimpleFiltersValue = { name?: string; value: string; count?: number };

type FilterKey = keyof typeof FilterConsts;

export type IFilterType = (typeof FilterConsts)[FilterKey]['type'];
type IFilterQuery = (typeof FilterConsts)[FilterKey]['query'];

export type IFilterObject = {
  temporaryValues?: ISimpleFiltersValue[];
  appliedValues?: ISimpleFiltersValue[];
  availableValues?: ISimpleFiltersValue[];
  type: IFilterType;
  query: IFilterQuery;
};

export interface ISimpleFiltersType {
  styles: IFilterObject;
  origins: IFilterObject;
  formats: IFilterObject;
  moods: IFilterObject;
  characteristics: IFilterObject;
}

export type TProductCatalogOrdering = 'price' | 'rank';
export type TProductCatalogToggle = true | false;
export interface IFilterTypesComplex {
  ordering: {
    availableValues: TProductCatalogOrdering[];
    temporaryValue?: TProductCatalogOrdering;
    appliedValue?: TProductCatalogOrdering;
    type: IFilterType;
    query: IFilterQuery;
  };
  priceRangeMax: {
    temporaryValue?: number;
    appliedValue?: number;
    availableValue?: number;
    type: IFilterType;
    query: IFilterQuery;
  };
  priceRangeMin: {
    temporaryValue?: number;
    appliedValue?: number;
    availableValue?: number;
    type: IFilterType;
    query: IFilterQuery;
  };
  rating: {
    availableValues?: IProductRatingOptions[];
    temporaryValues?: IProductRatingOptions[];
    appliedValues?: IProductRatingOptions[];
    type: IFilterType;
    query: IFilterQuery;
  };
  search: {
    temporaryValue: string;
    appliedValue: string;
    type: IFilterType;
    query: IFilterQuery;
  };
  wishlist: {
    type: IFilterType;
    availableValues: TProductCatalogToggle[];
    temporaryValue: boolean;
    appliedValue: boolean;
    query: IFilterQuery;
  };
  character: {
    availableValues: TProductCatalogToggle[];
    type: IFilterType;
    temporaryValue: boolean;
    appliedValue: boolean;
    query: IFilterQuery;
  };
  promotion: {
    type: IFilterType;
    availableValues: TProductCatalogToggle[];
    temporaryValue: boolean;
    appliedValue: boolean;
    query: IFilterQuery;
  };
}

export interface IFilterChangeAction {
  type: IFilterType;
  filterValue: ISimpleFiltersValue;
}

export interface IFilterState {
  simpleFilters?: ISimpleFiltersType;
  complexFilters: IFilterTypesComplex;
  isFilterOpened: boolean;
  filterApplyTrigger: number;
  isFilterStatsLoading: boolean;
  isFilterStatsLoaded: boolean;
  lastPriceRangeChangeWasManual?: boolean;
}

const initialState: IFilterState = {
  complexFilters: {
    character: {
      temporaryValue: false,
      appliedValue: false,
      availableValues: [true, false],
      type: FilterConsts.character.type,
      query: ApiQueries.character,
    },
    promotion: {
      temporaryValue: false,
      appliedValue: false,
      availableValues: [true, false],
      type: FilterConsts.promotion.type,
      query: FilterConsts.promotion.query,
    },
    wishlist: {
      temporaryValue: false,
      availableValues: [true, false],
      appliedValue: false,
      type: FilterConsts.wishlist.type,
      query: ApiQueries.productIdentifier,
    },
    rating: {
      availableValues: [],
      type: FilterConsts.rating.type,
      query: ApiQueries.productIdentifier,
    },
    priceRangeMin: {
      query: ApiQueries.priceMin,
      type: FilterConsts.priceRangeMin.type,
    },
    priceRangeMax: {
      query: ApiQueries.priceMax,
      type: FilterConsts.priceRangeMax.type,
    },
    ordering: {
      availableValues: [ApiValues.orderingByRank, ApiValues.orderingByPrice],
      temporaryValue: ApiValues.orderingByRank,
      appliedValue: ApiValues.orderingByRank,
      type: FilterConsts.ordering.type,
      query: ApiQueries.ordering,
    },
    search: {
      // ToDo test search
      temporaryValue: '',
      appliedValue: '',
      type: FilterConsts.search.type,
      query: FilterConsts.search.query,
    },
  },
  simpleFilters: {
    characteristics: {
      availableValues: [],
      temporaryValues: [],
      appliedValues: [],
      type: FilterConsts.characteristics.type,
      query: FilterConsts.characteristics.query,
    },
    formats: {
      availableValues: [],
      temporaryValues: [],
      appliedValues: [],
      type: FilterConsts.formats.type,
      query: FilterConsts.formats.query,
    },
    moods: {
      availableValues: [],
      temporaryValues: [],
      appliedValues: [],
      type: FilterConsts.moods.type,
      query: FilterConsts.moods.query,
    },
    origins: {
      availableValues: [],
      temporaryValues: [],
      appliedValues: [],
      type: FilterConsts.origins.type,
      query: FilterConsts.origins.query,
    },
    styles: {
      availableValues: [],
      temporaryValues: [],
      appliedValues: [],
      type: FilterConsts.styles.type,
      query: FilterConsts.styles.query,
    },
  },
  isFilterOpened: false,
  isFilterStatsLoading: false,
  isFilterStatsLoaded: false,
  filterApplyTrigger: null,
};

export const actionFetchFilterStats = createAsyncThunk(
  'actionFetchFilterStats',
  async ({ params }: IFetchProductsOptions) => {
    const response = await getProductsListData({ ...params });

    return {
      ...response,
      results: [],
    };
  },
);

const catalogFilter = createSlice({
  extraReducers: builder => {
    builder
      .addCase(actionFetchFilterStats.pending, state => {
        state.isFilterStatsLoading = true;
      })
      .addCase(actionGetProductRateOptions.fulfilled, (state, action) => {
        state.isFilterStatsLoading = false;
        state.complexFilters.rating.availableValues = [...(action.payload?.choices?.rate_options || [])].sort(
          (a, b) => a.rateValue - b.rateValue,
        );
      })
      .addMatcher(isAnyOf(actionFetchProducts.fulfilled, actionFetchFilterStats.fulfilled), (state, action) => {
        const stats = action.payload?.stats || {};
        Object.keys(stats).forEach(type => {
          const currentDataForThisType = Object.values(state.simpleFilters).find(value => value.type === type);

          if (currentDataForThisType && type !== FilterConsts.priceRangeMax.type) {
            currentDataForThisType.availableValues = Object.keys(stats[type]).map(filterValueKey => ({
              name: stats[type][filterValueKey]?.name,
              value: filterValueKey,
              count: stats[type][filterValueKey]?.count,
            }));
          } else if (type === FilterConsts.priceRangeMax.type) {
            const currentMaxValue = state.complexFilters.priceRangeMax.availableValue || 0;
            const currentMinValue = state.complexFilters.priceRangeMin.availableValue || 0;
            const statsMaxValue = stats[type].max ? Math.round(stats[type].max) : 0;
            const statsMinValue = stats[type].min ? Math.round(stats[type].min) : 0;

            state.complexFilters.priceRangeMax.availableValue =
              (!state.lastPriceRangeChangeWasManual || statsMaxValue > currentMaxValue) && statsMaxValue
                ? statsMaxValue
                : currentMaxValue;
            state.complexFilters.priceRangeMin.availableValue =
              (!state.lastPriceRangeChangeWasManual || statsMinValue < currentMinValue) && statsMinValue
                ? statsMinValue
                : currentMinValue;
          }
        });
        state.isFilterStatsLoaded = true;
        state.isFilterStatsLoading = false;
      });
  },
  initialState,
  name: 'catalogFilter',
  reducers: {
    applyCatalogFilter: {
      reducer: state => {
        state.isFilterOpened = false;
        state.filterApplyTrigger = Date.now();

        Object.values({ ...state.complexFilters, ...state.simpleFilters }).forEach(filter => {
          if ('temporaryValues' in filter) {
            filter.appliedValues = filter.temporaryValues;
          }
          if ('temporaryValue' in filter) {
            filter.appliedValue = filter.temporaryValue;
          }
        });
      },

      prepare: (options?: { withTracking?: boolean }) => ({
        payload: undefined,
        meta: { withTracking: options?.withTracking ?? true },
      }),
    },
    applyCatalogFilterOrdering: (state, action: PayloadAction<TProductCatalogOrdering>) => {
      state.complexFilters.ordering.temporaryValue = action.payload;
      state.filterApplyTrigger = new Date().getTime();
    },
    closeCatalogFilter: state => {
      state.isFilterOpened = false;
      Object.values({ ...state.complexFilters, ...state.simpleFilters }).forEach(filter => {
        if ('temporaryValues' in filter) {
          filter.temporaryValues = filter.appliedValues || [];
        }
        if ('temporaryValue' in filter) {
          filter.temporaryValue = filter.appliedValue;
        }
      });
    },
    openCatalogFilter: state => {
      state.isFilterOpened = true;
    },
    resetCatalogFilters: state => ({
      ...initialState,
      complexFilters: {
        ...initialState.complexFilters,
        rating: {
          ...initialState.complexFilters.rating,
          // Preserve loaded rating options
          availableValues: state.complexFilters.rating.availableValues,
        },
      },
    }),
    resetCatalogFilterApplyTrigger: state => {
      // Using for trigger new product request
      state.filterApplyTrigger = null;
    },
    setProductRatingFilterTemporaryValue: (state, action: PayloadAction<string>) => {
      const currentTemporaryValues = state.complexFilters.rating.temporaryValues || [];
      const isAlreadySelected = currentTemporaryValues?.some(rating => rating.slug === action.payload);
      const ratingObjectForPayloadSlug = state.complexFilters.rating.availableValues?.find(
        rating => rating.slug === action.payload,
      );

      state.complexFilters.rating.temporaryValues = isAlreadySelected
        ? currentTemporaryValues.filter(rating => rating.slug !== action.payload)
        : [...currentTemporaryValues, ratingObjectForPayloadSlug];
    },
    setCatalogFilterSearchText: (state, action: PayloadAction<string>) => {
      state.complexFilters.search.temporaryValue = action.payload;
    },
    resetCatalogFilterPriceRangeAppliedValues: state => {
      state.complexFilters.priceRangeMin = {
        ...initialState.complexFilters.priceRangeMin,
        availableValue: state.complexFilters.priceRangeMin.availableValue,
      };
      state.complexFilters.priceRangeMax = {
        ...initialState.complexFilters.priceRangeMax,
        availableValue: state.complexFilters.priceRangeMax.availableValue,
      };
      state.lastPriceRangeChangeWasManual = false;
    },
    setCatalogFilterPriceRangeTemporaryValues: (state, action: ISetPriceRangeValuesPayload) => {
      const { payload: { userLowerPriceRangeValue, userUpperPriceRangeValue, isManual = false } = {} } = action;
      state.lastPriceRangeChangeWasManual = isManual;
      state.complexFilters.priceRangeMax.temporaryValue = userUpperPriceRangeValue;
      state.complexFilters.priceRangeMin.temporaryValue = userLowerPriceRangeValue;
    },
    toggleWishListFilterTemporaryValue: state => {
      state.complexFilters.wishlist.temporaryValue = !state.complexFilters.wishlist.temporaryValue;
    },
    setCharactersToggleFilter: (state, action: PayloadAction<boolean>) => {
      state.complexFilters.character.temporaryValue = action.payload;
      if (!state.isFilterOpened) {
        state.complexFilters.character.appliedValue = action.payload;
      }
    },
    setSublistFilterTemporaryValues: (state, action: PayloadAction<IFilterChangeAction>) => {
      const { type, filterValue } = action.payload;
      const currentDataForThisType = Object.values(state.simpleFilters).find(value => value.type === type);
      currentDataForThisType.temporaryValues = currentDataForThisType.temporaryValues?.find(
        currentValue => currentValue.value === filterValue.value,
      )
        ? currentDataForThisType?.temporaryValues.filter(currentValue => currentValue.value !== filterValue.value)
        : [...currentDataForThisType?.temporaryValues, filterValue];
    },
    togglePromotionFilter: state => {
      state.complexFilters.promotion.temporaryValue = !state.complexFilters.promotion.temporaryValue;
    },
  },
});

export const {
  applyCatalogFilter,
  closeCatalogFilter,
  applyCatalogFilterOrdering,
  openCatalogFilter,
  resetCatalogFilters,
  resetCatalogFilterApplyTrigger,
  toggleWishListFilterTemporaryValue,
  togglePromotionFilter,
  setSublistFilterTemporaryValues,
  setCatalogFilterSearchText,
  setCharactersToggleFilter,
  resetCatalogFilterPriceRangeAppliedValues,
  setCatalogFilterPriceRangeTemporaryValues,
  setProductRatingFilterTemporaryValue,
} = catalogFilter.actions;

export default catalogFilter.reducer;
